import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getPhotographerByUrl } from '../api/apiClient';
import MasonryGrid from '../components/MasonryGrid';
import Seo from '../components/Seo';
import { SITE_URL } from '../seoConfig';
import { Loader2, Clock, AlertTriangle, Edit2, X, CheckCircle2, Camera, Calendar, Facebook, Link as LinkIcon, Image as ImageIcon, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { useTags } from '../context/TagsContext';
import { toTitleCase } from '../utils/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;
const BATCH_SIZE = 20;

const PhotographerPage = () => {
  const { userSlug, page: pageParam } = useParams();
  const initialPage = Math.max(1, parseInt(pageParam, 10) || 1);
  const { resolveTagSlug } = useTags();
  const [photographer, setPhotographer] = useState(null);
  const [allImages, setAllImages] = useState([]);
  const [displayedImages, setDisplayedImages] = useState([]);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pendingImages, setPendingImages] = useState([]);
  const [rejectedImages, setRejectedImages] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [totalViews, setTotalViews] = useState(0);
  const [memberSince, setMemberSince] = useState(null);
  const [availableForHire, setAvailableForHire] = useState(false);
  const [photographerTags, setPhotographerTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState({ open: false, image: null });
  const [editForm, setEditForm] = useState({});
  const [availableTags, setAvailableTags] = useState([]);

  useEffect(() => {
    axios.get(`${API_BASE}/tags`)
      .then(({ data }) => setAvailableTags(data.tags || []))
      .catch(() => {});
  }, []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (userSlug) {
        try {
          setLoading(true);
          const data = await getPhotographerByUrl(userSlug);
          setPhotographer(data.photographer);
          setAllImages(data.images);
          // Direct hit on /page/N shows only that page's slice (photos (N-1)*20 .. N*20)
          // so each paginated URL has unique content for SEO.
          const start = (initialPage - 1) * BATCH_SIZE;
          setDisplayedImages(data.images.slice(start, start + BATCH_SIZE));
          setCurrentPage(initialPage);
          setPendingImages(data.pending_images || []);
          setRejectedImages(data.rejected_images || []);
          setIsOwner(data.is_owner || false);
          setTotalViews(data.total_views);
          setMemberSince(data.member_since);
          setAvailableForHire(data.available_for_hire || false);
          setPhotographerTags(data.photographer_tags || []);
        } catch (err) {
          console.error('Error fetching photographer:', err);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSlug]);

  const sourceOffset = (currentPage - 1) * BATCH_SIZE + displayedImages.length;
  const hasMore = sourceOffset < allImages.length;
  const nextPage = currentPage + 1;
  const loadMoreHref = `/photos/photographer/${userSlug}/page/${nextPage}`;

  const loadMore = () => {
    const more = allImages.slice(sourceOffset, sourceOffset + BATCH_SIZE);
    setDisplayedImages((prev) => [...prev, ...more]);
    setCurrentPage(nextPage);
    window.history.pushState({}, '', loadMoreHref);
  };

  const openEditModal = (image) => {
    setEditForm({
      title: image.title || '',
      description: image.description || '',
      cats_name: (image.tags && image.tags.length ? image.tags.join(', ') : ''),
      cam_model: image.cam_model || '',
      cam_lens: image.cam_lens || '',
      cam_aperture: image.cam_aperture || '',
      cam_exp: image.cam_exp || '',
      cam_iso: image.cam_iso || '',
      cam_foc: image.cam_foc || ''
    });
    setEditModal({ open: true, image });
  };

  const handleEditSubmit = async () => {
    if (!editModal.image) return;

    const tagsClean = (editForm.cats_name || '').split(',').map((t) => t.trim()).filter(Boolean);
    if (tagsClean.length === 0) {
      alert('Please select at least one tag for your photo.');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.put(
        `${API_BASE}/images/${editModal.image.id}`,
        editForm,
        { withCredentials: true }
      );
      
      const data = await getPhotographerByUrl(userSlug);
      setAllImages(data.images);
      setDisplayedImages(data.images.slice(0, displayedImages.length));
      setPendingImages(data.pending_images || []);
      setRejectedImages(data.rejected_images || []);
      
      setEditModal({ open: false, image: null });
      
      if (response.data.resubmitted) {
        alert('Photo has been resubmitted for approval!');
      }
    } catch (error) {
      console.error('Error updating image:', error);
      alert(error.response?.data?.detail || 'Failed to update image');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
      </div>
    );
  }

  if (!photographer) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500">Photographer not found</p>
      </div>
    );
  }

  const photographerName = toTitleCase(photographer.user_display_name);
  const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;

  // JSON-LD: a single @graph (Organization + WebSite + Person + ProfilePage +
  // BreadcrumbList). Individual photos are NOT listed (the list is dynamic,
  // sorted by views), so they remain plain visible links/cards.
  const origin = SITE_URL;
  const userUrlPath = photographer.user_url || `/photos/photographer/${userSlug}`;
  const profileUrl = `${origin}${userUrlPath}`;
  const stripHtml = (html) => {
    if (!html) return '';
    if (typeof window === 'undefined') return String(html).replace(/<[^>]+>/g, '').trim();
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || '').trim();
  };
  const bio = stripHtml(photographer.user_description);
  const social = (photographer.user_social_url || '').trim();
  const pageTitlePlain = `${photographerName} — Mauritius Photographer${currentPage > 1 ? ` — Page ${currentPage}` : ''}`;
  const pageDescription = `View photos by ${photographerName} on Explora Mauritius, including island landscapes, beaches, nature, hotels, waterfalls, sunsets and other Mauritius photography.`;

  const personLd = {
    '@type': 'Person',
    '@id': `${profileUrl}#person`,
    name: photographerName,
    url: profileUrl,
    ...(photographer.user_image_url ? { image: photographer.user_image_url } : {}),
    ...(bio ? { description: bio } : {}),
    jobTitle: 'Photographer',
    ...(social ? { sameAs: [social] } : {}),
    ...(photographerTags.length ? { knowsAbout: photographerTags } : {}),
  };
  const profilePageLd = {
    '@type': 'ProfilePage',
    '@id': `${profileUrl}#webpage`,
    url: profileUrl,
    name: `${photographerName} | Mauritius Photographer on Explora`,
    description: pageDescription,
    isPartOf: { '@id': `${origin}/#website` },
    mainEntity: { '@id': `${profileUrl}#person` },
    inLanguage: 'en',
    breadcrumb: { '@id': `${profileUrl}#breadcrumb` },
  };
  const breadcrumbLd = {
    '@type': 'BreadcrumbList',
    '@id': `${profileUrl}#breadcrumb`,
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
      { '@type': 'ListItem', position: 2, name: 'Photographers', item: `${origin}/photographers-in-mauritius` },
      { '@type': 'ListItem', position: 3, name: photographerName, item: profileUrl },
    ],
  };
  const graphLd = {
    '@context': 'https://schema.org',
    '@graph': [
      { '@type': 'Organization', '@id': `${origin}/#organization`, name: 'Explora Mauritius', url: `${origin}/` },
      { '@type': 'WebSite', '@id': `${origin}/#website`, url: `${origin}/`, name: 'Explora Mauritius', publisher: { '@id': `${origin}/#organization` }, inLanguage: 'en' },
      personLd,
      profilePageLd,
      breadcrumbLd,
    ],
  };

  return (
    <div className="min-h-screen bg-white" data-testid="photographer-page">
      <Seo
        title={pageTitlePlain}
        description={(bio || pageDescription).slice(0, 280)}
        canonical={currentPage > 1 ? `${origin}${userUrlPath}/page/${currentPage}` : profileUrl}
        noindex={currentPage > 1}
        image={photographer.user_image_url}
        imageAlt={`${photographerName} Photographer on Explora Mauritius`}
        type="profile"
        jsonLd={[graphLd]}
      />
      {/* Photographer Header - centered, white background */}
      <div className="bg-white py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="flex flex-col md:flex-row items-center md:items-start space-y-6 md:space-y-0 md:space-x-8">
            {/* Profile Photo - 150x150, no outline */}
            <img
              src={photographer.user_image_url || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(photographerName)}
              alt={`${photographerName} Photographer on Explora Mauritius`}
              className="w-[150px] h-[150px] rounded-full object-cover flex-shrink-0"
              data-testid="photographer-photo"
            />

            {/* Info Section */}
            <div className="text-center md:text-left">
              {/* Name - Sentence Case H1 */}
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 tracking-tight" data-testid="photographer-name">
                {photographerName}
              </h1>

              {/* Description */}
              {photographer.user_description && (
                <div
                  className="text-gray-600 mb-4 max-w-xl prose prose-sm"
                  dangerouslySetInnerHTML={{ __html: photographer.user_description }}
                  data-testid="photographer-description"
                />
              )}

              {/* Stats / Social block — every row shares the same icon column width
                  and the same vertical spacing for a clean, aligned layout. */}
              <div className="flex flex-col items-center md:items-start space-y-2.5 text-sm">
                {/* Available for hire / Hobbyist */}
                <div className="flex items-center" data-testid="hire-status">
                  <span className="w-5 flex justify-center flex-shrink-0">
                    {availableForHire ? (
                      <CheckCircle2 className="w-4 h-4 text-blue-500" />
                    ) : (
                      <Camera className="w-4 h-4 text-gray-400" />
                    )}
                  </span>
                  <span className={`ml-3 ${availableForHire ? 'text-blue-600' : 'text-gray-500'}`}>
                    {availableForHire ? 'Available for hire' : 'Hobbyist photographer'}
                  </span>
                </div>

                {/* Member since */}
                <div className="flex items-center">
                  <span className="w-5 flex justify-center flex-shrink-0">
                    <Calendar className="w-4 h-4 text-gray-400" />
                  </span>
                  <span className="ml-3 text-gray-500">
                    Member since {memberSince
                      ? new Date(memberSince).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                      : '—'}
                  </span>
                </div>

                {/* Share on Facebook */}
                <a
                  href={facebookShareUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-gray-900 hover:text-black transition-colors"
                  data-testid="share-facebook"
                >
                  <span className="w-5 flex justify-center flex-shrink-0">
                    <Facebook className="w-4 h-4" />
                  </span>
                  <span className="ml-3">Share profile on Facebook</span>
                </a>

                {/* Connect with photographer */}
                {photographer.user_social_url && (
                  <a
                    href={photographer.user_social_url}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="flex items-center text-gray-900 hover:text-black transition-colors"
                    data-testid="connect-photographer"
                  >
                    <span className="w-5 flex justify-center flex-shrink-0">
                      <LinkIcon className="w-4 h-4" />
                    </span>
                    <span className="ml-3">Connect with {photographerName}</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar - full-width bottom border, content kept inside the page width */}
      <div className="w-full border-b border-gray-200">
        <div className="py-4 px-0 md:px-4 lg:px-8 max-w-full md:max-w-[70%] mx-auto">
          <div className="flex items-center space-x-8 px-4 md:px-0">
            <div className="flex items-center" data-testid="photo-count">
              <span className="w-5 flex justify-center flex-shrink-0">
                <ImageIcon className="w-4 h-4 text-gray-400" />
              </span>
              <span className="ml-2 text-sm text-gray-700">Photos {allImages.length}</span>
            </div>
            <div className="flex items-center" data-testid="view-count">
              <span className="w-5 flex justify-center flex-shrink-0">
                <Eye className="w-4 h-4 text-gray-400" />
              </span>
              <span className="ml-2 text-sm text-gray-700">Total Views {totalViews.toLocaleString()}</span>
            </div>
            {isOwner && pendingImages.length > 0 && (
              <div className="flex items-center" data-testid="pending-count">
                <span className="w-5 flex justify-center flex-shrink-0">
                  <Clock className="w-4 h-4 text-gray-400" />
                </span>
                <Link
                  to="/my-pending"
                  className="ml-2 text-sm text-yellow-600 hover:text-yellow-700 hover:underline"
                  data-testid="pending-link"
                >
                  {pendingImages.length} pending
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Visible breadcrumb — placed UNDER the photo/views stats bar. Uses the
          same layout as the Tag page breadcrumb. Mirrors the JSON-LD. */}
      <nav
        className="breadcrumb max-w-full md:max-w-[70%] mx-auto px-4 md:px-4 lg:px-8 py-3 text-sm text-gray-500"
        aria-label="Breadcrumb"
        data-testid="photographer-breadcrumb"
      >
        <ol className="breadcrumb-list flex flex-wrap items-center gap-1.5">
          <li><Link to="/" className="text-gray-500 no-underline hover:text-gray-900 transition-colors">Explora</Link></li>
          <li aria-hidden="true" className="text-gray-300">&gt;</li>
          <li>
            <Link to="/photographers-in-mauritius" className="text-gray-500 no-underline hover:text-gray-900 transition-colors">
              Photographers in Mauritius
            </Link>
          </li>
          <li aria-hidden="true" className="text-gray-300">&gt;</li>
          <li aria-current="page" className="text-gray-700 font-medium">{photographerName}</li>
        </ol>
      </nav>

      {/* Published Images Grid */}
      <div className="py-8">
        {displayedImages.length > 0 ? (
          <MasonryGrid
            images={displayedImages}
            showPhotographer={false}
            showTags={true}
            hasMore={hasMore}
            onLoadMore={loadMore}
            loadMoreHref={loadMoreHref}
            prioritizeFirst={true}
          />
        ) : (
          <div className="text-center py-12">
            {isOwner && pendingImages.length > 0 ? (
              <p className="text-gray-500" data-testid="pending-empty-message">
                Your first photos are waiting for admin approval. This profile is not public yet.
              </p>
            ) : (
              <>
                <p className="text-gray-500">No published photos yet</p>
                {isOwner && (
                  <Link to="/upload">
                    <Button className="mt-4">Upload Your First Photo</Button>
                  </Link>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Tags Section */}
      {photographerTags.length > 0 && (
        <div className="py-12 px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-testid="tags-heading">
              {photographerName}&apos;s work appears in the following categories
            </h2>
            <div className="flex flex-wrap gap-2" data-testid="photographer-tags">
              {photographerTags.map((tag, idx) => {
                const slug = resolveTagSlug(tag);
                if (!slug) {
                  return (
                    <span
                      key={idx}
                      className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded"
                      data-testid={`photographer-tag-${idx}`}
                    >
                      {tag}
                    </span>
                  );
                }
                return (
                  <Link
                    key={idx}
                    to={`/photos-hd/${slug}`}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded transition-colors"
                    data-testid={`photographer-tag-${idx}`}
                  >
                    {tag}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModal.open && editModal.image && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit Photo</h3>
              <button
                onClick={() => setEditModal({ open: false, image: null })}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <img
              src={editModal.image.image_urls?.thumbnail}
              alt={editModal.image.title}
              className="w-full h-48 object-cover rounded-lg mb-4"
            />
            
            {editModal.image.rejection_reason && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm font-medium text-red-800">Admin Feedback:</p>
                <p className="text-sm text-red-700">{editModal.image.rejection_reason}</p>
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <Input
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  placeholder="Photo title"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <Textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  placeholder="Photo description"
                  rows={3}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md p-2 grid grid-cols-2 gap-1" data-testid="edit-tags-list">
                  {availableTags.map((tag) => {
                    const selected = (editForm.cats_name || '').split(',').map((t) => t.trim()).filter(Boolean);
                    const isChecked = selected.includes(tag.cat_name);
                    return (
                      <label key={tag.cat_id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {
                            const next = isChecked
                              ? selected.filter((t) => t !== tag.cat_name)
                              : [...selected, tag.cat_name];
                            setEditForm({ ...editForm, cats_name: next.join(', ') });
                          }}
                          data-testid={`edit-tag-${tag.cat_id}`}
                        />
                        <span className="text-gray-700">{tag.cat_name}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {(editForm.cats_name || '').trim() ? `Selected: ${editForm.cats_name}` : 'Pick at least one tag from the list.'}
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Camera</label>
                  <Input
                    value={editForm.cam_model}
                    onChange={(e) => setEditForm({ ...editForm, cam_model: e.target.value })}
                    placeholder="Canon EOS R5"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lens</label>
                  <Input
                    value={editForm.cam_lens}
                    onChange={(e) => setEditForm({ ...editForm, cam_lens: e.target.value })}
                    placeholder="24-70mm f/2.8"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Aperture</label>
                  <Input
                    value={editForm.cam_aperture}
                    onChange={(e) => setEditForm({ ...editForm, cam_aperture: e.target.value })}
                    placeholder="f/2.8"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Shutter</label>
                  <Input
                    value={editForm.cam_exp}
                    onChange={(e) => setEditForm({ ...editForm, cam_exp: e.target.value })}
                    placeholder="1/500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ISO</label>
                  <Input
                    value={editForm.cam_iso}
                    onChange={(e) => setEditForm({ ...editForm, cam_iso: e.target.value })}
                    placeholder="400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Focal</label>
                  <Input
                    value={editForm.cam_foc}
                    onChange={(e) => setEditForm({ ...editForm, cam_foc: e.target.value })}
                    placeholder="50mm"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setEditModal({ open: false, image: null })}
              >
                Cancel
              </Button>
              <Button
                onClick={handleEditSubmit}
                disabled={saving || !editForm.title}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : editModal.image.published === 2 ? (
                  'Save & Resubmit'
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotographerPage;
